// Browser-side glue for the P2P signaling broker (docs/p2p.md). Two halves that
// speak the broker's wire protocol (packages/broker/src/protocol.ts, kept in
// lockstep) so the WebRTC handshake can travel between two browsers that can't
// reach each other directly:
//
//   - brokerJoinSignal(): a WebRtcBusSocket signalOffer. It relays the joiner's
//     offer through the broker and resolves with the host's answer, so the
//     joiner connects to a P2P host with the SAME WebRtcBusSocket it uses for a
//     hosted server — only the signaling path differs (a broker relay instead
//     of an HTTP POST).
//   - BrokerHostSession: the host side. It registers with the broker, exposes
//     the issued session id (for the share link), and hands every incoming
//     offer to acceptOffer (a PeerHost), relaying each answer back.
//
// Both default to the global WebSocket (the browser's, and Node's on the test
// runtime); the factory seam lets a unit test inject a fake.

// WebSocket.OPEN, as a literal so this needs no reference to a WebSocket global
// at module load (and works against an injected fake).
const WS_OPEN = 1;

export interface BrokerSignalDeps {
  // Defaults to `(url) => new WebSocket(url)`.
  createWebSocket?: (url: string) => WebSocket;
}

function parse(data: unknown): Record<string, unknown> | null {
  if (typeof data !== "string") return null;
  try { return JSON.parse(data) as Record<string, unknown>; } catch { return null; }
}

// A WebRtcBusSocket signalOffer that relays through the broker: open a socket,
// send the offer for `session`, resolve with the answer (or reject on error /
// early close). One socket per handshake — it closes as soon as it settles.
export function brokerJoinSignal(
  brokerUrl: string,
  session: string,
  deps: BrokerSignalDeps = {},
): (offerSdp: string) => Promise<string> {
  const make = deps.createWebSocket ?? ((u) => new WebSocket(u));
  return (offerSdp) => new Promise<string>((resolve, reject) => {
    let settled = false;
    const ws = make(brokerUrl);
    const settle = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      fn();
      try { ws.close(); } catch { /* already gone */ }
    };
    ws.addEventListener("open", () => ws.send(JSON.stringify({ t: "join", session, offer: offerSdp })));
    ws.addEventListener("message", (ev: MessageEvent) => {
      const msg = parse(ev.data);
      if (!msg) return;
      if (msg.t === "answer" && typeof msg.answer === "string") {
        const answer = msg.answer;
        settle(() => resolve(answer));
      } else if (msg.t === "error") {
        const message = typeof msg.message === "string" ? msg.message : "broker error";
        settle(() => reject(new Error(message)));
      }
    });
    ws.addEventListener("error", () => settle(() => reject(new Error("broker connection failed"))));
    ws.addEventListener("close", () => settle(() => reject(new Error("broker closed before answer"))));
  });
}

// The host's broker connection. Registers as a host, surfaces the issued session
// id via `session`, and relays each joiner's offer→answer through acceptOffer
// (typically a PeerHost). Lives for the session's lifetime; closing it ends the
// session (the broker reaps it when this socket drops).
export class BrokerHostSession {
  private readonly ws: WebSocket;
  private sessionId: string | null = null;
  private readonly sessionPromise: Promise<string>;
  private resolveSession!: (id: string) => void;
  private rejectSession!: (e: Error) => void;
  private closed = false;

  constructor(
    brokerUrl: string,
    private readonly acceptOffer: (offerSdp: string) => Promise<string>,
    deps: BrokerSignalDeps = {},
  ) {
    this.sessionPromise = new Promise((res, rej) => {
      this.resolveSession = res;
      this.rejectSession = rej;
    });
    const make = deps.createWebSocket ?? ((u) => new WebSocket(u));
    this.ws = make(brokerUrl);
    this.ws.addEventListener("open", () => this.ws.send(JSON.stringify({ t: "host" })));
    this.ws.addEventListener("message", (ev: MessageEvent) => this.onMessage(ev));
    this.ws.addEventListener("error", () => {
      if (!this.sessionId) this.rejectSession(new Error("broker connection failed"));
    });
    this.ws.addEventListener("close", () => {
      if (!this.sessionId) this.rejectSession(new Error("broker closed before hosting"));
    });
  }

  // Resolves with the session id to put in the share link.
  get session(): Promise<string> {
    return this.sessionPromise;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    try { this.ws.close(); } catch { /* already gone */ }
  }

  private onMessage(ev: MessageEvent): void {
    const msg = parse(ev.data);
    if (!msg) return;
    if (msg.t === "hosted" && typeof msg.session === "string") {
      this.sessionId = msg.session;
      this.resolveSession(msg.session);
    } else if (msg.t === "offer" && typeof msg.from === "string" && typeof msg.offer === "string") {
      const from = msg.from;
      // Answer this joiner's offer, then relay the answer back keyed by peer id.
      // A failed accept just drops that one joiner — the session stays up.
      void this.acceptOffer(msg.offer).then(
        (answer) => this.send({ t: "answer", to: from, answer }),
        (e: unknown) => console.error(`[broker-host] acceptOffer failed for peer ${from}:`, e),
      );
    }
  }

  private send(obj: unknown): void {
    if (this.ws.readyState === WS_OPEN) this.ws.send(JSON.stringify(obj));
  }
}
